import { Component, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-marketing-header',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <header class="header" [class.scrolled]="isScrolled">
      <div class="container header-content">
        <a (click)="scrollToSection('home')" class="logo">
          <img src="/logos/logo_final.png" alt="Suntur Quri Wasi" class="logo-img" />
          <span class="logo-text">SUNTUR QURI WASI</span>
        </a>

        <nav class="nav-links" [class.active]="menuOpen">
          @for (item of navItems; track item.section) {
            <a (click)="scrollToSection(item.section)" 
               class="nav-link" 
               [class.active]="activeSection === item.section">
              {{ item.label }}
            </a>
          }
          
          <a routerLink="/reservar" class="btn-cta">Reservar</a>
        </nav>

        <button class="menu-toggle" (click)="toggleMenu()" [attr.aria-expanded]="menuOpen">
          <span class="menu-icon" [class.open]="menuOpen">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>
    </header>
  `,
  styles: [`
    .header {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: transparent;
      z-index: 1000;
      padding: 12px 0;
      transition: all 0.3s ease;

      &.scrolled {
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(10px);
        box-shadow: 0 2px 20px rgba(0,0,0,0.08);
        padding: 8px 0;

        .logo-text { color: var(--color-primary); }
        .nav-link { color: #4a4a4a; }
        .btn-cta {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }
        .menu-icon span { background: var(--color-primary); }
      }
    }

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 12px;
      text-decoration: none;
      cursor: pointer;
    }

    .logo-img {
      height: 38px;
      width: auto;
    }

    .logo-text {
      font-family: var(--font-heading);
      font-weight: 600;
      font-size: 1rem;
      letter-spacing: 0.5px;
      color: var(--color-white);
      transition: color 0.3s ease;
    }

    .nav-links {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .nav-link {
      text-decoration: none;
      color: rgba(255, 255, 255, 0.9);
      font-family: var(--font-body);
      font-weight: 500;
      font-size: 0.9rem;
      padding: 8px 14px;
      border-radius: 6px;
      transition: all 0.2s ease;
      cursor: pointer;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: 4px;
        left: 50%;
        width: 0;
        height: 2px;
        background: currentColor;
        transition: all 0.2s ease;
        transform: translateX(-50%);
      }

      &:hover::after, &.active::after {
        width: calc(100% - 28px);
      }

      &:hover, &.active {
        color: var(--color-white);
      }
    }

    .header.scrolled .nav-link {
      &:hover, &.active {
        color: var(--color-primary);
      }
    }

    /* Ghost Button CTA */
    .btn-cta {
      display: inline-flex;
      align-items: center;
      background: transparent;
      color: var(--color-white);
      border: 1.5px solid rgba(255, 255, 255, 0.7);
      padding: 8px 20px;
      border-radius: 6px;
      text-decoration: none;
      font-family: var(--font-body);
      font-weight: 600;
      font-size: 0.9rem;
      letter-spacing: 0.3px;
      transition: all 0.2s ease;
      margin-left: 8px;

      &:hover {
        background: var(--color-white);
        color: var(--color-primary);
        border-color: var(--color-white);
      }
    }

    .header.scrolled .btn-cta {
      border-color: var(--color-primary);
      color: var(--color-primary);

      &:hover {
        background: var(--color-primary);
        color: var(--color-white);
        border-color: var(--color-primary);
      }
    }

    .menu-toggle {
      display: none;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 8px;
    }

    .menu-icon {
      display: flex;
      flex-direction: column;
      gap: 5px;
      width: 22px;

      span {
        display: block;
        height: 2px;
        background: var(--color-white);
        border-radius: 2px;
        transition: all 0.2s ease;
      }

      &.open span:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
      &.open span:nth-child(2) { opacity: 0; }
      &.open span:nth-child(3) { transform: rotate(-45deg) translate(5px, -5px); }
    }

    @media (max-width: 992px) {
      .menu-toggle { display: block; }

      .nav-links {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        flex-direction: column;
        justify-content: center;
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(10px);
        padding: 24px;
        display: none;
        gap: 8px;
        opacity: 0;
        transition: opacity 0.3s ease;

        &.active { 
          display: flex; 
          opacity: 1;
        }

        .nav-link {
          color: #4a4a4a;
          font-size: 1.1rem;
          padding: 16px 24px;
          width: 100%;
          text-align: center;

          &::after { display: none; }

          &:hover, &.active {
            color: var(--color-primary);
            background: rgba(200, 106, 74, 0.08);
          }
        }

        .btn-cta {
          margin: 24px 0 0;
          padding: 14px 32px;
          font-size: 1rem;
          border-color: var(--color-primary);
          color: var(--color-primary);
          
          &:hover {
            background: var(--color-primary);
            color: white;
          }
        }
      }

      .header.scrolled .menu-icon span {
        background: var(--color-primary);
      }
    }
  `]
})
export class MarketingHeaderComponent {
  private router = inject(Router);

  menuOpen = false;
  isScrolled = false;
  activeSection = 'home';

  // Optimized nav: removed "Inicio" (logo does that), renamed for clarity
  navItems = [
    { label: 'Habitaciones', section: 'habitaciones' },
    { label: 'Experiencias', section: 'caracteristicas' },
    { label: 'Galería', section: 'descubre' },
    { label: 'Contacto', section: 'contacto' }
  ];

  constructor() {
    this.checkScroll();
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    this.checkScroll();
    this.updateActiveSection();
  }

  private checkScroll(): void {
    this.isScrolled = window.pageYOffset > 50;
  }

  private updateActiveSection(): void {
    const scrollPos = window.pageYOffset + 100;

    for (const item of this.navItems) {
      const element = document.getElementById(item.section);
      if (element) {
        const top = element.offsetTop;
        const bottom = top + element.offsetHeight;
        if (scrollPos >= top && scrollPos < bottom) {
          this.activeSection = item.section;
          break;
        }
      }
    }
  }

  scrollToSection(sectionId: string): void {
    this.menuOpen = false;
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }
}
